import React from 'react';

interface SubtextWordBlocksProps {
  dailyWord: string;
  usedPositions: Set<number>;
  displayOrder: number[];
  onCharacterClick: (char: string, actualPosition: number) => void;
  disabled?: boolean;
  isRandomized?: boolean;
}

export default function SubtextWordBlocks({ 
  dailyWord, 
  usedPositions, 
  displayOrder,
  onCharacterClick,
  disabled = false,
  isRandomized = false
}: SubtextWordBlocksProps) {
  // Track which actual positions have been clicked to prevent double-firing
  const [clickedActualPositions, setClickedActualPositions] = React.useState<Set<number>>(new Set());

  const handleClick = React.useCallback((char: string, actualPosition: number) => {
    // Check if button is disabled
    const isDisabled = usedPositions.has(actualPosition) || disabled;
    if (isDisabled) {
      return;
    }
    
    // Prevent double-firing on touch devices
    if (clickedActualPositions.has(actualPosition)) {
      return;
    }

    // Mark this actual position as being processed
    setClickedActualPositions(prev => new Set(prev).add(actualPosition));

    // Call the original handler
    onCharacterClick(char, actualPosition);

    // Clear the processing flag after a short delay
    setTimeout(() => {
      setClickedActualPositions(prev => {
        const newSet = new Set(prev);
        newSet.delete(actualPosition);
        return newSet;
      });
    }, 100); // 100ms should be enough to prevent double-clicks but not interfere with normal gameplay
  }, [onCharacterClick, clickedActualPositions, usedPositions, disabled]);

  return (
    <div className="subtext-word-blocks">
      {displayOrder.map((actualPosition, displayIndex) => {
        const char = dailyWord[actualPosition];
        const isDisabled = usedPositions.has(actualPosition) || disabled;
        
        return (
          <button
            key={displayIndex}
            className={`character-block ${isDisabled ? 'disabled' : ''} ${isRandomized ? 'scrambled' : ''}`}
            onMouseDown={() => handleClick(char, actualPosition)}
            onTouchStart={() => handleClick(char, actualPosition)}
            disabled={isDisabled}
          >
            {char.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
} 